/**
 * useTimezoneFormatter
 * ====================
 * A centralized React hook for formatting dates/times across the entire app.
 *
 * Behavior:
 * - For tenant users: reads `tenant.timezone` from the auth context and formats
 *   all dates in that configured timezone (e.g. "Asia/Kolkata").
 * - For normal users (no tenant): falls back to the browser's local system timezone.
 *
 * Usage:
 *   const { formatDate, formatTime, formatWithTz } = useTimezoneFormatter();
 *   <span>{formatDate(row.created_at)}</span>
 */

import { useCallback } from 'react';
import { useAuth } from '../context/useAuth';
import {
    formatDateTime,
    formatToLocalTime24h,
    formatWithTimezone,
    formatRelativeTime as formatRelative,
} from '../utils/dateTime';
import { isTenantScoped } from '../utils/permissions';

export function useTimezoneFormatter() {
    // useAuth() returns the full auth context including user object and browser timezone
    let auth;
    try {
        auth = useAuth();
    } catch {
        auth = null;
    }

    // Priority:
    // 1. branding.timezone (Set by admin in branding/onboarding) - PRIMARY SOURCE
    // 2. tenant.timezone from the user profile (legacy/sync)
    // 3. browser-detected IANA timezone (auto-detected on login)
    // 4. undefined → browser local timezone (Intl.DateTimeFormat default)
    const tenantTimezone =
        auth?.branding?.timezone ||     // Branding source of truth (User Request)
        auth?.tenantTimezone ||         // Active tenant-level preference from state
        auth?.user?.tenantTimezone ||   // Fallback: tenant-level preference from user object
        null;

    const userPreferenceTimezone =
        auth?.user?.timezone ||         // Individual user-level preference
        null;

    const browserTimezone = auth?.timezone || null;  // Auto-detected (e.g. "Asia/Kolkata")

    // Determine if we should apply tenant-level localization.
    // We use the permission-based scope check rather than just a DOM class 
    // to ensure parity between Flow Studio and Platform Logs.
    const isTenant = auth?.user && isTenantScoped(auth.user);

    // Final Resolution
    const tz = (isTenant && tenantTimezone)
        ? tenantTimezone
        : (userPreferenceTimezone || browserTimezone || undefined);

    /** Format UTC date value to full date + time string (supports Intl options) */
    const formatDate = useCallback(
        (dateVal, options = null) => formatDateTime(dateVal, tz, options),
        [tz]
    );

    /** Format UTC date value to time-only string */
    const formatTime = useCallback(
        (dateVal) => formatToLocalTime24h(dateVal, tz),
        [tz]
    );

    /** Format UTC date value with timezone abbreviation (e.g. "26/03/2026, 18:00 IST") */
    const formatWithTz = useCallback(
        (dateVal) => formatWithTimezone(dateVal, tz),
        [tz]
    );

    /** Format UTC date value as relative time (e.g. "5m ago") */
    const formatRelativeTime = useCallback(
        (dateVal) => formatRelative(dateVal, tz),
        [tz]
    );

    return { 
        formatDate, 
        formatTime, 
        formatWithTz, 
        formatRelativeTime, 
        timezone: tz 
    };
}
